import React, { useState } from 'react';
import { ArrowLeftIcon, EditIcon, DownloadIcon, FileTextIcon, UserIcon, CalendarIcon, DollarSignIcon, MinusSquareIcon, CheckCircleIcon, AlertCircleIcon, LinkIcon, ExternalLinkIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { NoteVisibility } from '../common/StandardizedNotes';
import { GlobeIcon, UsersIcon, LockIcon, MessageSquareIcon } from 'lucide-react';
export function CreditNoteDetail({
  creditNote,
  onBack,
  onEdit,
  locale = 'en-US'
}) {
  // Function to determine badge color based on status
  const getStatusBadgeClass = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Posted':
        return 'bg-blue-100 text-blue-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Added state for related communications
  const [relatedCommunications, setRelatedCommunications] = useState([{
    id: 4,
    title: 'Office Supplies Return - Credit Note Clarification',
    timestamp: '2023-10-19T16:20:00Z',
    messageCount: 1,
    lastMessage: "Credit note received for returned items from last month's order. Quality issues were reported to the vendor."
  }]);
  // Function to render note visibility icon and label
  const renderNoteVisibility = () => {
    if (!creditNote.noteVisibility) return null;
    const getVisibilityIcon = () => {
      switch (creditNote.noteVisibility) {
        case 'Public':
          return <GlobeIcon className="h-4 w-4 text-green-500" data-id="element-3413" />;
        case 'Internal':
          return <UsersIcon className="h-4 w-4 text-blue-500" data-id="element-3414" />;
        case 'Role-based':
          return <UsersIcon className="h-4 w-4 text-purple-500" data-id="element-3415" />;
        case 'User-specific':
          return <UserIcon className="h-4 w-4 text-orange-500" data-id="element-3416" />;
        case 'Creator-only':
          return <LockIcon className="h-4 w-4 text-red-500" data-id="element-3417" />;
        default:
          return null;
      }
    };
    const getVisibilityLabel = () => {
      switch (creditNote.noteVisibility) {
        case 'Public':
          return 'Public - Visible to everyone';
        case 'Internal':
          return 'Internal - Visible to all internal users';
        case 'Role-based':
          return `Role-based - Visible to specific roles: ${creditNote.noteVisibilityDetails || 'None specified'}`;
        case 'User-specific':
          return `User-specific - Visible to specific users: ${creditNote.noteVisibilityDetails || 'None specified'}`;
        case 'Creator-only':
          return 'Private - Visible only to creator';
        default:
          return '';
      }
    };
    return <div className="flex items-center text-xs text-gray-500 mt-1" data-id="element-3418">
        {getVisibilityIcon()}
        <span className="ml-1" data-id="element-3419">{getVisibilityLabel()}</span>
      </div>;
  };
  // Group credit note lines by invoice
  const linesByInvoice = {};
  if (creditNote.lines) {
    creditNote.lines.forEach(line => {
      if (line.linkedInvoiceId) {
        if (!linesByInvoice[line.linkedInvoiceId]) {
          linesByInvoice[line.linkedInvoiceId] = [];
        }
        linesByInvoice[line.linkedInvoiceId].push(line);
      }
    });
  }
  // Get invoice details by ID
  const getInvoiceById = invoiceId => {
    return creditNote.linkedInvoices.find(inv => inv.id === invoiceId) || null;
  };
  return <div className="bg-white shadow-lg rounded-lg overflow-hidden" data-id="element-3420">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center" data-id="element-3421">
        <h3 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-3422">
          <MinusSquareIcon className="h-5 w-5 mr-2 text-green-600" data-id="element-3423" />
          Credit Note Details
        </h3>
        <div className="flex space-x-2" data-id="element-3424">
          <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3425">
            <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-3426" />
            Back to List
          </button>
          {(creditNote.status === 'Draft' || creditNote.status === 'Rejected') && <button onClick={() => onEdit(creditNote)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3427">
              <EditIcon className="h-4 w-4 mr-1" data-id="element-3428" />
              Edit
            </button>}
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3429">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-3430" />
            Download PDF
          </button>
        </div>
      </div>
      <div className="p-6" data-id="element-3431">
        {/* Header Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-3432">
          <div data-id="element-3433">
            <div className="flex items-center mb-4" data-id="element-3434">
              <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3435" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3436">
                Credit Note Information
              </h4>
            </div>
            <div className="bg-gray-50 rounded-md p-4 space-y-3" data-id="element-3437">
              <div className="flex justify-between" data-id="element-3438">
                <span className="text-sm font-medium text-gray-500" data-id="element-3439">
                  Credit Note Number:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3440">
                  {creditNote.creditNoteNumber}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3441">
                <span className="text-sm font-medium text-gray-500" data-id="element-3442">
                  Status:
                </span>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(creditNote.status)}`} data-id="element-3443">
                  {creditNote.status}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3444">
                <span className="text-sm font-medium text-gray-500" data-id="element-3445">Date:</span>
                <span className="text-sm text-gray-900" data-id="element-3446">
                  {new Date(creditNote.creditNoteDate).toLocaleDateString(locale)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3447">
                <span className="text-sm font-medium text-gray-500" data-id="element-3448">
                  Reference:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3449">
                  {creditNote.reference || 'N/A'}
                </span>
              </div>
            </div>
          </div>
          <div data-id="element-3450">
            <div className="flex items-center mb-4" data-id="element-3451">
              <UserIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3452" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3453">
                Vendor Information
              </h4>
            </div>
            <div className="bg-gray-50 rounded-md p-4 space-y-3" data-id="element-3454">
              <div className="flex justify-between" data-id="element-3455">
                <span className="text-sm font-medium text-gray-500" data-id="element-3456">
                  Vendor Name:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3457">
                  {creditNote.vendor}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3458">
                <span className="text-sm font-medium text-gray-500" data-id="element-3459">
                  Currency:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3460">
                  {creditNote.currency}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3461">
                <span className="text-sm font-medium text-gray-500" data-id="element-3462">
                  Total Amount:
                </span>
                <span className="text-sm text-gray-900 font-semibold" data-id="element-3463">
                  {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3464">
                <span className="text-sm font-medium text-gray-500" data-id="element-3465">
                  Applied Amount:
                </span>
                <div className="flex items-center" data-id="element-3466">
                  {creditNote.appliedAmount > 0 ? creditNote.unappliedAmount > 0 ? <AlertCircleIcon className="h-4 w-4 text-yellow-500 mr-1" data-id="element-3467" /> : <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-3468" /> : <AlertCircleIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-3469" />}
                  <span className="text-sm text-gray-900" data-id="element-3470">
                    {formatCurrency(creditNote.appliedAmount || 0, creditNote.currency, locale)}
                  </span>
                </div>
              </div>
              <div className="flex justify-between" data-id="element-3471">
                <span className="text-sm font-medium text-gray-500" data-id="element-3472">
                  Unapplied Amount:
                </span>
                <span className="text-sm text-gray-900" data-id="element-3473">
                  {formatCurrency(creditNote.unappliedAmount || 0, creditNote.currency, locale)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Line Items */}
        <div className="mt-6" data-id="element-3474">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-3475">Line Items</h3>
          <div className="mt-4 overflow-x-auto" data-id="element-3476">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3477">
              <thead className="bg-gray-50" data-id="element-3478">
                <tr data-id="element-3479">
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3480">
                    Description
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3481">
                    Quantity
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3482">
                    Unit Price
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3483">
                    Tax Code
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3484">
                    Tax Amount
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3485">
                    Amount
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3486">
                    Invoice Ref
                  </th>
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3487">
                    Invoice Line Ref
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3488">
                {creditNote.lines.map((line, index) => <tr key={index} data-id="element-3489">
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900" data-id="element-3490">
                      {line.description}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3491">
                      {line.quantity}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3492">
                      {formatCurrency(line.unitPrice, creditNote.currency, locale)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3493">
                      {line.taxCode} ({line.taxRate}%)
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3494">
                      {formatCurrency(line.taxAmount, creditNote.currency, locale)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3495">
                      {formatCurrency(line.amount, creditNote.currency, locale)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3496">
                      {line.invoiceRef ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3497">
                          {line.invoiceRef}
                        </span> : <span className="text-gray-400" data-id="element-3498">—</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-3499">
                      {line.invoiceLineRef ? <span className="text-gray-700" data-id="element-3500">
                          {line.invoiceLineRef}
                        </span> : <span className="text-gray-400" data-id="element-3501">—</span>}
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-gray-50" data-id="element-3502">
                <tr data-id="element-3503">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-500" data-id="element-3504">
                    Subtotal:
                  </td>
                  <td colSpan={4} className="px-3 py-2 text-sm text-gray-900" data-id="element-3505">
                    {formatCurrency(creditNote.lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0), creditNote.currency, locale)}
                  </td>
                </tr>
                <tr data-id="element-3506">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-500" data-id="element-3507">
                    Tax Total:
                  </td>
                  <td colSpan={4} className="px-3 py-2 text-sm text-gray-900" data-id="element-3508">
                    {formatCurrency(creditNote.lines.reduce((sum, line) => sum + (line.taxAmount || 0), 0), creditNote.currency, locale)}
                  </td>
                </tr>
                <tr data-id="element-3509">
                  <td colSpan={4} className="px-3 py-2 text-right text-sm font-medium text-gray-900" data-id="element-3510">
                    Total:
                  </td>
                  <td colSpan={4} className="px-3 py-2 text-sm font-bold text-gray-900" data-id="element-3511">
                    {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        {/* Linked Invoices */}
        <div className="mb-8" data-id="element-3512">
          <div className="flex items-center mb-4" data-id="element-3513">
            <LinkIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3514" />
            <h4 className="text-lg font-medium text-gray-900" data-id="element-3515">
              Linked Invoices
            </h4>
            <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-id="element-3516">
              Reference only
            </span>
          </div>
          {creditNote.linkedInvoices && creditNote.linkedInvoices.length > 0 ? <div className="overflow-x-auto" data-id="element-3517">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md" data-id="element-3518">
                <thead className="bg-gray-50" data-id="element-3519">
                  <tr data-id="element-3520">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3521">
                      Invoice Number
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3522">
                      Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3523">
                      Amount
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3524">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3525">
                  {creditNote.linkedInvoices.map(invoice => <tr key={invoice.id} data-id="element-3526">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-3527">
                        {invoice.invoiceNumber || invoice.number}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-id="element-3528">
                        {new Date(invoice.invoiceDate || invoice.date).toLocaleDateString(locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-3529">
                        {formatCurrency(invoice.amount, invoice.currency || creditNote.currency, locale)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm" data-id="element-3530">
                        <button className="text-indigo-600 hover:text-indigo-900 text-sm flex items-center" data-id="element-3531">
                          <ExternalLinkIcon className="h-4 w-4 mr-1" data-id="element-3532" />
                          View Invoice
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="bg-gray-50 p-4 text-center rounded-md border border-gray-200" data-id="element-3533">
              <p className="text-gray-500" data-id="element-3534">
                No invoices linked to this credit note
              </p>
            </div>}
        </div>
        {/* Notes */}
        {creditNote.notes && <div className="mb-8" data-id="element-3535">
            <div className="flex items-center mb-4" data-id="element-3536">
              <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3537" />
              <h4 className="text-lg font-medium text-gray-900" data-id="element-3538">Notes</h4>
            </div>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-3539">
              {renderNoteVisibility()}
              <p className="text-sm text-gray-700 mt-2" data-id="element-3540">{creditNote.notes}</p>
            </div>
          </div>}
        {/* Related Communications - New Section */}
        {relatedCommunications.length > 0 && <div className="mb-8" data-id="element-3541">
            <div className="flex items-center justify-between mb-4" data-id="element-3542">
              <div className="flex items-center" data-id="element-3543">
                <MessageSquareIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-3544" />
                <h4 className="text-lg font-medium text-gray-900" data-id="element-3545">
                  Related Communications
                </h4>
              </div>
              <a href="#" className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center" data-id="element-3546">
                View All
                <ExternalLinkIcon className="h-4 w-4 ml-1" data-id="element-3547" />
              </a>
            </div>
            <div className="bg-gray-50 rounded-md border border-gray-200 divide-y divide-gray-200" data-id="element-3548">
              {relatedCommunications.map(comm => <div key={comm.id} className="p-4 hover:bg-gray-100" data-id="element-3549">
                  <div className="flex justify-between" data-id="element-3550">
                    <h5 className="text-sm font-medium text-indigo-600" data-id="element-3551">
                      {comm.title}
                    </h5>
                    <span className="text-xs text-gray-500" data-id="element-3552">
                      {new Date(comm.timestamp).toLocaleDateString(locale)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2" data-id="element-3553">
                    {comm.lastMessage}
                  </p>
                  <div className="mt-2 flex justify-between items-center" data-id="element-3554">
                    <span className="text-xs text-gray-500" data-id="element-3555">
                      {comm.messageCount}{' '}
                      {comm.messageCount === 1 ? 'message' : 'messages'}
                    </span>
                    <a href="#" className="text-xs text-indigo-600 hover:text-indigo-800" data-id="element-3556">
                      View Thread
                    </a>
                  </div>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
}